import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import LedgerTable from './table';
import ApiService from '@/services/api';
import dayjs from "dayjs";
import { exportLedgerToExcel } from '@/helpers/utils';


const Ledger = () => {
  const [report, setReport] = useState([]);
  const [coaList, setCoaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cooperative, setCooperative] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Load orders on component mount
  useEffect(() => {
    loadData();
    fetchCOA();
  }, []);

  const [filters, setFilters] = useState({
    startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
    account_id: null,
  });

  const exportExcel = async () => {
    // Check if account_id is selected
    if (!filters.account_id) {
      Swal.fire({
        icon: 'warning',
        title: 'Perhatian!',
        text: 'Silakan pilih perkiraan terlebih dahulu sebelum melakukan export',
      });
      return;
    }

    // Check if there's data to export
    if (!report.transactions || report.transactions.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Tidak Ada Data',
        text: 'Tidak ada data untuk di-export',
      });
      return;
    }

    // Pass transactions and summary with the new structure
    exportLedgerToExcel(
      report.transactions || [],
      report.summary || {},
      dayjs(filters.startDate).format('DD-MM-YYYY'),
      dayjs(filters.endDate).format('DD-MM-YYYY')
    )
  }

  const fetchCOA = async () => {
    try {
      setLoading(true);
      const profile = JSON.parse(localStorage.getItem('user_profile'));
      const cooperativeId = profile?.cooperative.id || 0;
      setCooperative(profile?.cooperative);
      const response = await ApiService.getCOA(cooperativeId)
      if (response.status === 200) {
        const sortedData = response.data
        setCoaList(sortedData.map(item => {
          return { value: item.id, name: `${item.account_code} - ${item.account_name}` };
        }));
      }
    } catch (error) {
      setCoaList([]);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Something went wrong!',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const profile = JSON.parse(localStorage.getItem('user_profile'));
      const cooperativeId = profile?.cooperative.id || 0;
      const response = await ApiService.getLedger(
        cooperativeId,
        filters.startDate,
        filters.endDate,
        filters.account_id,
      )
      if (response.status === 200) {
        setReport(response.data);
      }
    } catch (error) {
      setReport([]);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Something went wrong!',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
              </svg>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
              </svg>
              <button
                onClick={() => navigate('/admin/list-principal-savings')}
                className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
              >
                Buku Besar
              </button>
            </div>
          </li>
        </ol>
      </nav>
    
      <div className="flex flex-auto flex-col">
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h4 className="card-title">Buku Besar</h4>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                />
                <select
                  id="account_id"
                  name="account_id"
                  value={filters.account_id}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      account_id: e.target.value,
                    })
                  }
                  className="w-full px-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                >
                  <option value="">Pilih Perkiraan</option>
                  {coaList?.map((res) => (
                    <option key={res.value} value={res.value}>
                      {res.name}
                    </option>
                  ))}
                </select>
                <button
                  className="btn bg-blue-600 text-white px-4 py-2 rounded"
                  onClick={() => loadData(1)}
                  disabled={loading}
                >
                  <i className="mgc_search_line me-2"></i>Filter
                </button>
                <button
                  className="btn bg-green-600 text-white px-4 py-2 rounded"
                  onClick={() => exportExcel()}
                  disabled={loading}
                >
                  <i className="mgc_download_2_line me-2"></i>Export
                </button>
                <button
                  className="btn btn-sm bg-secondary text-white"
                  onClick={() => loadData(1)}
                  disabled={loading}
                >
                  <i className="mgc_refresh_1_line me-2"></i>Refresh
                </button>
              </div>
            </div>
          </div>

          {cooperative && (
            <div className="text-center my-6 print:block">
              <h2 className="text-xl font-bold">{cooperative.name}</h2>
              <div className="mt-4">
                <h3 className="text-lg font-bold">Buku Besar</h3>
                <p className="text-sm">{dayjs(filters.startDate).format('DD MMMM YYYY')} - {dayjs(filters.endDate).format('DD MMMM YYYY')}</p>
              </div>
            </div>
          )}
          
          <div className="p-6">
            <LedgerTable data={report?.transactions || []} dataTransaction={report?.summary || []} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Ledger;